/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-search-input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */

    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      } else {
        this.input.value = this.getQuery();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      this.getResults(searchTerm);
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */

    async getProductMatches(searchTerm, signal) {
      // Use the exact same query, catalogue, page size and default ordering as /search.
      try {
        const params = new URLSearchParams({collection:'all',country:'AU',currency:'AUD',q:searchTerm});
        const response = await fetch('/apps/hobbytech-discovery?'+params,{credentials:'same-origin',cache:'no-store',signal});
        if (!response.ok) throw Error('Catalogue unavailable');
        const data = await response.json(), age = Date.now()-Date.parse(data.capturedAt);
        if (data.country!=='AU'||data.currency!=='AUD'||!Number.isFinite(age)||age<0||age>2700000||!Number.isSafeInteger(data.total)||data.total<0||data.page!==1||data.pageSize!==24||data.pages!==Math.ceil(data.total/24)||!Array.isArray(data.items)||data.items.length>24) throw Error('Invalid catalogue');
        for (const product of data.items) {
          const url = new URL(product.url,location.origin);
          if(url.origin!==location.origin||!/^\/products\/[^/?#]+$/.test(url.pathname)||typeof product.title!=='string'||typeof product.id!=='string'||!Number.isSafeInteger(product.priceMinor)||product.priceMinor<0)throw Error('Invalid product');
        }
        if(data.total>0)return data.items;
      } catch(error) { if(signal.aborted)throw error; }
      // The full page keeps Shopify's native grid on zero results or an outage.
      const params = new URLSearchParams({q:searchTerm,type:'product','options[prefix]':'last'});
      const response = await fetch('/search?'+params,{credentials:'same-origin',signal});
      if(!response.ok)throw Error('Search unavailable');
      const doc = new DOMParser().parseFromString(await response.text(),'text/html');
      const fallback = doc.querySelector('[data-hbt-fallback]');
      if(!fallback)throw Error('Native search unavailable');
      return [...fallback.querySelectorAll('[data-hbt-native-card]')].map(card=>({
        id:card.dataset.productId,title:card.dataset.productTitle,
        url:card.dataset.productUrl?.split('?')[0],
        image:card.querySelector('.card__main-image')?.getAttribute('data-src')||card.querySelector('.card__main-image')?.getAttribute('src'),
        priceText:card.querySelector('.price__current')?.textContent.trim(),
        stockText:card.querySelector('.product-inventory__status')?.textContent.trim(),
        stockKind:card.dataset.available==='false'?'out-of-stock':card.dataset.preorder==='true'?'preorder':null,
        native:true
      }));
    }

    async getResults(searchTerm) {
      const requestId=(this.requestId||0)+1;this.requestId=requestId;
      this.searchController?.abort();this.searchController=new AbortController();
      const signal=AbortSignal.any([this.searchController.signal,AbortSignal.timeout(20000)]);
      this.setLiveRegionLoadingState();
      const make=(tag,className,text)=>{const el=document.createElement(tag);el.className=className;if(text!==undefined)el.textContent=text;return el;};
      try {
        // Retain theme styles, but use the full results page for product matching.
        const params=new URLSearchParams({q:searchTerm,'resources[type]':['product',theme.settings.pSearchShowCollections&&'collection',theme.settings.pSearchShowPages&&'page',theme.settings.pSearchShowArticles&&'article'].filter(Boolean).join(','),'resources[limit]':theme.settings.pSearchLimit,'resources[limit_scope]':theme.settings.pSearchLimitScope,section_id:'predictive-search'});
        const nativeRequest=fetch(`${theme.routes.predictiveSearch}?${params}`,{signal}).then(async response=>{
          if(!response.ok)return null;
          return new DOMParser().parseFromString(await response.text(),'text/html').querySelector('#shopify-section-predictive-search');
        }).catch(()=>null);
        const [products,native]=await Promise.all([this.getProductMatches(searchTerm,signal),nativeRequest]);
        if(requestId!==this.requestId)return;
        const root=make('div',''),shell=make('div','predictive-search absolute bg-theme-bg text-theme-text text-start'),panel=make('div','predictive-search__results predictive-search__tabpanel focus-inset'),list=make('ul','predictive-search__list');
        for(const style of native?.querySelectorAll('style')??[])root.append(style.cloneNode(true));
        panel.setAttribute('role','tabpanel');panel.setAttribute('aria-label','Products');list.setAttribute('role','list');panel.append(list);shell.append(panel);root.append(shell);
        const limit=Math.min(24,Number(theme.settings.pSearchLimit)||10);
        for(const product of products.slice(0,limit)){
          const url=new URL(product.url,location.origin);
          if(url.origin!==location.origin||!/^\/products\/[^/?#]+$/.test(url.pathname)||typeof product.title!=='string')continue;
          const variant=product.matchingVariantIds?.[0];if(typeof variant==='string'&&/^\d{1,24}$/.test(variant))url.searchParams.set('variant',variant);
          const existing=native?.querySelector(`[data-hbt-product="${CSS.escape(String(product.id))}"]`);
          if(existing){const copy=existing.cloneNode(true);copy.querySelector('.js-search-link').href=url.pathname+url.search;list.append(copy);continue;}
          const item=make('li','predictive-search__item'),link=make('a','predictive-result flex items-start focus-inset js-search-link'),info=make('div','predictive-result__info flex-auto');
          item.setAttribute('role','option');item.dataset.hbtProduct=product.id;link.href=url.pathname+url.search;
          if(product.image){const imageUrl=new URL(product.image,location.origin);if(imageUrl.protocol==='https:'&&['cdn.shopify.com',location.hostname].includes(imageUrl.hostname)){imageUrl.searchParams.set('width','112');const media=make('div','predictive-result__media media relative'),img=make('img','predictive-result__media');img.src=imageUrl.href;img.alt=product.title;img.width=112;img.height=112;media.append(img);link.append(media);}}
          info.append(make('h3','predictive-result__title h6',product.title));
          const priceStock=make('div','hbt-predictive-price-stock'),price=make('div','price');
          price.append(make('strong','price__current',product.priceText||new Intl.NumberFormat('en-AU',{style:'currency',currency:'AUD'}).format(product.priceMinor/100)));priceStock.append(price);
          const stock=make('div','predictive-result__stock text-sm',product.stockText||'');if(product.stockKind)stock.classList.add('predictive-result__stock--'+product.stockKind);priceStock.append(stock);
          info.append(priceStock);link.append(info);item.append(link);list.append(item);
          // Stock labels hydrate independently, without delaying or reordering matches.
          if(!product.native){url.searchParams.set('section_id','hobbytech-discovery-card');
            fetch(url,{credentials:'same-origin',signal:AbortSignal.any([this.searchController.signal,AbortSignal.timeout(4000)])}).then(async response=>{
              if(!response.ok)return;
              const card=new DOMParser().parseFromString(await response.text(),'text/html').querySelector('[data-hbt-native-card]');
              if(card?.dataset.productId!==String(product.id)||requestId!==this.requestId)return;
              const target=this.results.querySelector(`[data-hbt-product="${CSS.escape(String(product.id))}"] .predictive-result__stock`);
              if(target){const status=card.querySelector('.product-inventory__status');target.textContent=card.dataset.preorder==='true'&&card.dataset.available==='true'?card.dataset.preorderLabel:status?.textContent.trim()||'';const level=status?.dataset.inventoryLevel;const kind=card.dataset.available==='false'?'out-of-stock':card.dataset.preorder==='true'?'preorder':level==='special_order'?'special-order':['normal','in_stock','low','very_low'].includes(level)?'in-stock':null;if(kind)target.classList.add('predictive-result__stock--'+kind);}
            }).catch(()=>{});
          }
        }
        if(!products.length)list.append(make('li','predictive-search__item','No products found.'));
        const submit=make('li','predictive-search__item js-submit'),button=make('button','predictive-search__view-all link w-full text-start focus-inset has-ltr-icon js-search-link','View all search results →');submit.setAttribute('role','option');button.type='submit';submit.append(button);list.append(submit);
        // Preserve the theme's tabs, spacing and non-product results.
        const tabs=native?.querySelector('tabbed-content');
        if(tabs){
          const originalPanel=tabs.querySelector('#psearch-products');
          if(originalPanel)originalPanel.replaceChildren(list);
          else {
            const tab=make('button','tablist__tab font-bold','Products');tab.type='button';tab.id='psearch-products-tab';tab.setAttribute('role','tab');tab.setAttribute('aria-controls','psearch-products');
            panel.id='psearch-products';panel.className='predictive-search__tabpanel focus-inset';panel.setAttribute('aria-labelledby',tab.id);panel.tabIndex=0;
            tabs.querySelector('[role=tablist]').prepend(tab);tabs.append(panel);
          }
          for(const tab of tabs.querySelectorAll('[role=tab]')){const selected=tab.id==='psearch-products-tab';tab.setAttribute('aria-selected',String(selected));tab.tabIndex=selected?0:-1;}
          for(const pane of tabs.querySelectorAll('[role=tabpanel]'))pane.hidden=pane.id!=='psearch-products';
          const wrapper=make('div','predictive-search__results');wrapper.append(tabs);shell.replaceChildren(wrapper);
        }
        root.querySelectorAll('.predictive-search__item').forEach((item,index)=>{item.id=`${this.input.id}-result-${index}`;});
        if(requestId===this.requestId&&this.getQuery()===searchTerm)this.renderResults(root.innerHTML.replace(/psearch/g,this.input.id));
      }catch(error){if(requestId===this.requestId)this.close();}
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.statusEl.textContent = '';
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      this.searchController?.abort();
      this.requestId = (this.requestId || 0) + 1;
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
